import axios from 'axios';

// Local Development or Production API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request Interceptor: Attach JWT token from localStorage to every outgoing request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Handle token expiration / 401 Unauthorized errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Clear authentication credentials if unauthorized
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userEmail');
      
      // Reload page to prompt login screen if not already on login
      if (window.location.pathname !== '/login') {
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// Authentication APIs
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Translation APIs (Restaurant website)
export const translationApi = {
  getAll: () => api.get('/translations'),
  getBySection: (section) => api.get(`/translations/section/${section}`),
  getFormatted: () => api.get('/translations/formatted'),
  create: (data) => api.post('/translations', data),
  bulkCreate: (translations) => api.post('/translations/bulk', { translations }),
  update: (id, data) => api.put(`/translations/${id}`, data),
  delete: (id) => api.delete(`/translations/${id}`),
};

// Customer Translation APIs (Customer website)
export const customerTranslationApi = {
  getAll: (section) => api.get('/customer-translations', { params: section ? { section } : {} }),
  getSections: () => api.get('/customer-translations/sections'),
  getBySection: (section) => api.get(`/customer-translations/section/${section}`),
  getExport: (section) => api.get('/customer-translations/export', { params: section ? { section } : {} }),
  create: (data) => api.post('/customer-translations', data),
  bulkCreate: (translations) => api.post('/customer-translations/bulk', { translations }),
  update: (id, data) => api.put(`/customer-translations/${id}`, data),
  delete: (id) => api.delete(`/customer-translations/${id}`),
};

// Image APIs
export const imageApi = {
  getAll: () => api.get('/images'),
  getBySection: (section) => api.get(`/images/section/${section}`),
  getByName: (name) => api.get(`/images/name/${name}`),
  getFormatted: () => api.get('/images/formatted'),
  upload: (formData) => api.post('/images/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, data) => api.put(`/images/${id}`, data),
  replace: (id, formData) => api.put(`/images/${id}/replace`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/images/${id}`),
};

export default api;
